import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { loft, superellipse, tube, makePeg } from './geom.js';

const BODY_H = 0.255;
const NECK_H = 0.195;
const BOX_H = 0.118;
const HEAD_H = 0.055;
const TOTAL = BODY_H + NECK_H + BOX_H + HEAD_H;

function widthAt(t) {
  const a = 0.172;
  const b = 0.182;
  const c = 0.136;
  const d = 0.15;
  const e = 0.118;
  if (t < 0.18) return THREE.MathUtils.lerp(a, b, t / 0.18);
  if (t < 0.48) return THREE.MathUtils.lerp(b, c, (t - 0.18) / 0.3);
  if (t < 0.78) return THREE.MathUtils.lerp(c, d, (t - 0.48) / 0.3);
  return THREE.MathUtils.lerp(d, e, (t - 0.78) / 0.22);
}

function centerX(t) {
  const waist = Math.sin(Math.max(0, (t - 0.15) * Math.PI) * 0.9);
  return 0.01 * waist;
}

function depthAt(t) {
  return 0.118 - Math.abs(t - 0.4) * 0.018;
}

function createBody(mats) {
  const group = new THREE.Group();
  const slices = 22;
  const profiles = [];
  for (let i = 0; i < slices; i++) {
    const v = i / (slices - 1);
    let y;
    let rx;
    let rz;
    let nF = 10;
    let nB = 2.35;
    if (v < 0.08) {
      const u = v / 0.08;
      y = THREE.MathUtils.lerp(0.006, 0.028, u);
      rx = widthAt(0) * 0.5 * THREE.MathUtils.lerp(0.35, 1, u);
      rz = depthAt(0) * 0.5 * THREE.MathUtils.lerp(0.45, 1, u);
      nB = 2.1;
    } else {
      const t = (v - 0.08) / 0.92;
      y = 0.028 + t * (BODY_H - 0.028);
      rx = widthAt(t) * 0.5;
      rz = depthAt(t) * 0.5;
    }
    profiles.push({
      y,
      points: superellipse(rx, rz, nF, nB, 48, centerX(v < 0.08 ? 0 : (v - 0.08) / 0.92)),
    });
  }
  const body = new THREE.Mesh(loft(profiles), mats.wood);
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const skinShape = new THREE.Shape();
  const left = [];
  const right = [];
  for (let i = 0; i <= 28; i++) {
    const t = i / 28;
    const y = 0.034 + t * (BODY_H - 0.05);
    const w = widthAt(t) - 0.018;
    const cx = centerX(t);
    left.push(new THREE.Vector2(cx - w / 2, y));
    right.push(new THREE.Vector2(cx + w / 2, y));
  }
  skinShape.moveTo(left[0].x, left[0].y);
  left.forEach((p) => skinShape.lineTo(p.x, p.y));
  for (let i = right.length - 1; i >= 0; i--) skinShape.lineTo(right[i].x, right[i].y);
  skinShape.closePath();

  const skinGeo = new THREE.ShapeGeometry(skinShape, 12);
  const pos = skinGeo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const t = THREE.MathUtils.clamp((y - 0.034) / (BODY_H - 0.05), 0, 1);
    const nx = x / Math.max(0.02, widthAt(t) * 0.5);
    const ny = (t - 0.5) * 2;
    const bulge = (1 - nx * nx) * (1 - ny * ny * 0.55) * 0.007;
    pos.setZ(i, bulge);
  }
  skinGeo.computeVertexNormals();
  const skin = new THREE.Mesh(skinGeo, mats.skin);
  skin.position.z = depthAt(0.45) * 0.5 + 0.001;
  skin.castShadow = true;
  skin.receiveShadow = true;
  group.add(skin);

  const rim = new THREE.Mesh(
    new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3(
        [...left, ...right.slice().reverse(), left[0]].map((p) => new THREE.Vector3(p.x, p.y, skin.position.z + 0.0008)),
        true,
        'catmullrom',
        0.02,
      ),
      90,
      0.0024,
      6,
      true,
    ),
    mats.woodDark,
  );
  rim.castShadow = true;
  group.add(rim);

  const stitchPts = [];
  const rimCurve = [...left, ...right.slice().reverse()];
  for (let i = 0; i < rimCurve.length; i += 2) {
    const p = rimCurve[i];
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.0011, 6, 6), mats.gutMid);
    m.position.set(p.x, p.y, skin.position.z + 0.0022);
    stitchPts.push(m);
    group.add(m);
  }

  return { group, skinZ: skin.position.z, skin };
}

function createNeck(mats) {
  const group = new THREE.Group();
  const y0 = BODY_H - 0.01;
  const geo = new RoundedBoxGeometry(0.112, NECK_H + 0.02, 0.086, 6, 0.012);
  const neck = new THREE.Mesh(geo, mats.wood);
  neck.position.set(0.004, y0 + NECK_H / 2, -0.004);
  neck.castShadow = true;
  neck.receiveShadow = true;
  group.add(neck);

  const shoulder = new THREE.Mesh(new RoundedBoxGeometry(0.128, 0.034, 0.092, 5, 0.014), mats.wood);
  shoulder.position.set(0.004, y0 + 0.004, -0.002);
  shoulder.castShadow = true;
  group.add(shoulder);

  const cavity = new THREE.Mesh(new RoundedBoxGeometry(0.086, NECK_H - 0.02, 0.042, 4, 0.008), mats.inner);
  cavity.position.set(0.004, y0 + NECK_H / 2 + 0.004, -0.038);
  group.add(cavity);

  const faceZ = 0.04;
  for (const x of [-0.046, 0.054]) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.006, NECK_H - 0.02, 0.0022), mats.bone);
    strip.position.set(x, y0 + NECK_H / 2, faceZ);
    group.add(strip);
  }
  const mid = new THREE.Mesh(new THREE.BoxGeometry(0.004, NECK_H - 0.03, 0.0018), mats.bone);
  mid.position.set(0.004, y0 + NECK_H / 2, faceZ);
  group.add(mid);

  const sundari = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 7; col++) {
      const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.0034, 0.0034, 0.0024, 14), mats.bone);
      ring.rotation.x = Math.PI / 2;
      const x = -0.028 + row * 0.02;
      const y = y0 + 0.028 + col * 0.024;
      ring.position.set(x, y, faceZ + 0.001);
      group.add(ring);
      const hole = new THREE.Mesh(new THREE.CircleGeometry(0.0014, 10), mats.inner);
      hole.position.set(x, y, faceZ + 0.0023);
      group.add(hole);
      sundari.push(new THREE.Vector3(x, y, faceZ + 0.002));
    }
  }

  const nut = new THREE.Mesh(new RoundedBoxGeometry(0.07, 0.007, 0.009, 3, 0.0015), mats.bone);
  nut.position.set(0.004, y0 + NECK_H - 0.012, faceZ + 0.004);
  group.add(nut);

  return { group, faceZ, sundari, nutY: nut.position.y, pegSideX: 0.062 };
}

function createPegbox(mats) {
  const group = new THREE.Group();
  const y0 = BODY_H + NECK_H - 0.012;
  const lower = new THREE.Mesh(new RoundedBoxGeometry(0.108, 0.062, 0.078, 5, 0.01), mats.wood);
  lower.position.set(0.003, y0 + 0.032, -0.006);
  lower.castShadow = true;
  group.add(lower);

  const cavity = new THREE.Mesh(new RoundedBoxGeometry(0.084, 0.046, 0.05, 3, 0.008), mats.inner);
  cavity.position.set(0.003, y0 + 0.034, -0.02);
  group.add(cavity);

  const makeOpening = (x, y) => {
    const hole = new THREE.Mesh(new THREE.CapsuleGeometry(0.009, 0.016, 6, 10), mats.inner);
    hole.rotation.z = Math.PI / 2;
    hole.position.set(x, y, 0.034);
    group.add(hole);
    const lip = new THREE.Mesh(new THREE.TorusGeometry(0.011, 0.0016, 8, 18), mats.woodDark);
    lip.position.copy(hole.position);
    group.add(lip);
  };
  makeOpening(-0.022, y0 + 0.034);
  makeOpening(0.028, y0 + 0.034);

  const upper = new THREE.Mesh(new RoundedBoxGeometry(0.1, 0.056, 0.07, 5, 0.01), mats.wood);
  upper.position.set(0.003, y0 + 0.088, -0.008);
  upper.castShadow = true;
  group.add(upper);

  const upperCav = new THREE.Mesh(new RoundedBoxGeometry(0.078, 0.04, 0.036, 3, 0.006), mats.inner);
  upperCav.position.set(0.003, y0 + 0.09, -0.018);
  group.add(upperCav);

  const plate = new THREE.Mesh(new THREE.BoxGeometry(0.078, 0.04, 0.004), mats.woodDark);
  plate.position.set(0.003, y0 + 0.09, 0.024);
  group.add(plate);

  const smallPegs = [];
  for (let col = 0; col < 2; col++) {
    for (let row = 0; row < 5; row++) {
      const peg = new THREE.Mesh(makePeg({ length: 0.028, shaftR: 0.0024, headR: 0.0055, headLen: 0.01 }), mats.peg);
      const x = -0.028 + col * 0.03 + (row % 2) * 0.004;
      const y = y0 + 0.072 + row * 0.0085;
      peg.position.set(x, y, 0.02);
      peg.rotation.y = -Math.PI / 2;
      peg.castShadow = true;
      group.add(peg);
      smallPegs.push(new THREE.Vector3(x, y, 0.03));
    }
  }

  const largePegs = [];
  const largeSpecs = [
    { x: -0.056, y: y0 + 0.022, z: 0.01, rot: Math.PI },
    { x: -0.056, y: y0 + 0.044, z: -0.012, rot: Math.PI },
    { x: 0.062, y: y0 + 0.022, z: 0.01, rot: 0 },
    { x: 0.062, y: y0 + 0.044, z: -0.012, rot: 0 },
  ];
  largeSpecs.forEach((s) => {
    const peg = new THREE.Mesh(makePeg({ length: 0.052, shaftR: 0.005, headR: 0.013, headLen: 0.02 }), mats.peg);
    peg.position.set(s.x, s.y, s.z);
    peg.rotation.y = s.rot;
    peg.castShadow = true;
    group.add(peg);
    largePegs.push(new THREE.Vector3(s.x + Math.cos(s.rot) * 0.02, s.y, s.z));
  });

  const tabij = new THREE.Mesh(new RoundedBoxGeometry(0.062, 0.006, 0.008, 2, 0.0012), mats.bone);
  tabij.position.set(0.003, y0 + 0.058, 0.034);
  group.add(tabij);

  return { group, largePegs, smallPegs, y0, tabijY: tabij.position.y };
}

function createHead(mats) {
  const group = new THREE.Group();
  const y = BODY_H + NECK_H + BOX_H - 0.02;
  const block = new THREE.Mesh(new RoundedBoxGeometry(0.062, 0.042, 0.05, 4, 0.01), mats.wood);
  block.position.set(0.003, y + 0.02, -0.004);
  block.castShadow = true;
  group.add(block);

  const crest = new THREE.Mesh(new THREE.SphereGeometry(0.02, 18, 14, 0, Math.PI * 2, 0, Math.PI * 0.65), mats.wood);
  crest.position.set(0.003, y + 0.04, 0.004);
  crest.scale.set(1.15, 0.9, 0.85);
  crest.castShadow = true;
  group.add(crest);

  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.008, 0.022, 10), mats.woodDark);
  beak.rotation.x = Math.PI / 2.4;
  beak.position.set(0.003, y + 0.03, 0.028);
  group.add(beak);

  const eye = new THREE.Mesh(new THREE.SphereGeometry(0.0032, 10, 8), mats.bone);
  eye.position.set(-0.01, y + 0.038, 0.016);
  group.add(eye);
  const eye2 = eye.clone();
  eye2.position.x = 0.016;
  group.add(eye2);
  return group;
}

function createElephant(mats) {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.01, 20, 16), mats.bone);
  body.scale.set(1.85, 1.05, 0.72);
  body.position.set(0, 0.014, 0);
  g.add(body);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.0078, 16, 14), mats.bone);
  head.scale.set(1.15, 1.05, 0.8);
  head.position.set(0.016, 0.016, 0);
  g.add(head);

  const trunk = new THREE.Mesh(
    new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.022, 0.014, 0),
        new THREE.Vector3(0.028, 0.008, 0.001),
        new THREE.Vector3(0.026, 0.002, 0.001),
        new THREE.Vector3(0.02, 0.001, 0),
      ]),
      14,
      0.0026,
      8,
      false,
    ),
    mats.bone,
  );
  g.add(trunk);

  for (const [x, z] of [
    [-0.009, 0.0048],
    [-0.009, -0.0048],
    [0.008, 0.0048],
    [0.008, -0.0048],
  ]) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.0024, 0.0028, 0.012, 8), mats.bone);
    leg.position.set(x, 0.006, z);
    g.add(leg);
  }

  const ear = new THREE.Mesh(new THREE.SphereGeometry(0.0064, 12, 10), mats.bone);
  ear.scale.set(0.28, 1.15, 0.95);
  ear.position.set(0.013, 0.02, 0.007);
  g.add(ear);
  const ear2 = ear.clone();
  ear2.position.z = -0.007;
  g.add(ear2);

  const tusk = new THREE.Mesh(new THREE.ConeGeometry(0.0014, 0.007, 8), mats.bone);
  tusk.rotation.z = Math.PI / 1.15;
  tusk.position.set(0.022, 0.011, 0.003);
  g.add(tusk);
  const tusk2 = tusk.clone();
  tusk2.position.z = -0.003;
  g.add(tusk2);

  g.traverse((o) => {
    if (o.isMesh) o.castShadow = true;
  });
  return g;
}

function createTailpiece(mats) {
  const g = new THREE.Group();
  const shelf = new THREE.Mesh(new RoundedBoxGeometry(0.072, 0.018, 0.028, 3, 0.004), mats.woodDark);
  shelf.position.set(0.004, 0.014, 0.055);
  shelf.castShadow = true;
  g.add(shelf);
  const plate = new THREE.Mesh(new THREE.BoxGeometry(0.068, 0.014, 0.0024), mats.bone);
  plate.position.set(0.004, 0.014, 0.07);
  g.add(plate);
  return g;
}

function createSidePegs(mats, neck) {
  const g = new THREE.Group();
  const anchors = [];
  for (let row = 0; row < 3; row++) {
    for (let i = 0; i < 7; i++) {
      const peg = new THREE.Mesh(makePeg({ length: 0.038, shaftR: 0.003, headR: 0.0072, headLen: 0.013 }), mats.peg);
      const y = BODY_H + 0.03 + i * 0.024;
      const z = 0.012 - row * 0.016;
      peg.position.set(neck.pegSideX, y, z);
      peg.castShadow = true;
      g.add(peg);
      anchors.push(new THREE.Vector3(neck.pegSideX - 0.008, y, z));
    }
  }
  return { group: g, anchors };
}

function createStrings(mats, ctx) {
  const group = new THREE.Group();
  const { skinZ, sundari, nutY, largePegs, smallPegs, tabijY } = ctx;
  const bridgeY = 0.118;
  const bridgeZ = skinZ + 0.03;
  const tailY = 0.02;
  const tailZ = 0.07;
  const nutZ = 0.048;

  const mainSpecs = [
    { x: 0.018, r: 0.00155, mat: mats.gutThin, name: 'sur', open: 1, label: 'Sur  Sa' },
    { x: 0.002, r: 0.00195, mat: mats.gutMid, name: 'pancham', open: 0.75, label: 'Pancham  Pa' },
    { x: -0.014, r: 0.0024, mat: mats.gutThick, name: 'kharaj', open: 0.5, label: 'Kharaj  Sa' },
  ];

  const playStrings = mainSpecs.map((spec, i) => {
    const start = new THREE.Vector3(spec.x, tailY, tailZ);
    const mid = new THREE.Vector3(spec.x * 0.9, bridgeY, bridgeZ + 0.001);
    const nut = new THREE.Vector3(spec.x * 0.7 + 0.004, nutY, nutZ);
    const peg = largePegs[Math.min(i, largePegs.length - 1)].clone();
    peg.x = THREE.MathUtils.lerp(peg.x, spec.x, 0.35);
    const geo = tube([start, mid, nut, peg], spec.r, 8, 36);
    const mesh = new THREE.Mesh(geo, spec.mat.clone());
    mesh.castShadow = true;
    group.add(mesh);

    const hit = new THREE.Mesh(tube([start, mid, nut], spec.r * 7, 6, 16), new THREE.MeshBasicMaterial({ visible: false }));
    hit.userData.stringIndex = i;
    group.add(hit);

    return {
      index: i,
      name: spec.name,
      label: spec.label,
      open: spec.open,
      mesh,
      hit,
      points: { start, mid, nut },
      material: mesh.material,
    };
  });

  const sympathetics = [];
  sundari.forEach((p, i) => {
    const bx = -0.03 + (i % 9) * 0.007;
    const start = new THREE.Vector3(bx, 0.028, 0.068);
    const br = new THREE.Vector3(bx, bridgeY - 0.004, skinZ + 0.018);
    const geo = tube([start, br, p], 0.00028, 4, 14);
    const mesh = new THREE.Mesh(geo, mats.steel);
    group.add(mesh);
    sympathetics.push(mesh);
  });

  smallPegs.forEach((p, i) => {
    const x = -0.026 + (i % 6) * 0.01;
    const start = new THREE.Vector3(x, tabijY, 0.038);
    const mesh = new THREE.Mesh(tube([start, p], 0.00026, 4, 10), mats.steel);
    group.add(mesh);
    sympathetics.push(mesh);
  });

  for (let i = 0; i < 9; i++) {
    const x = 0.048;
    const y1 = BODY_H + 0.02 + i * 0.02;
    const y0 = 0.04 + i * 0.01;
    const mesh = new THREE.Mesh(
      tube(
        [new THREE.Vector3(x, y0, 0.068), new THREE.Vector3(x - 0.01, y1, 0.042), new THREE.Vector3(0.058, y1 + 0.01, 0.01)],
        0.00026,
        4,
        10,
      ),
      mats.steel,
    );
    group.add(mesh);
    sympathetics.push(mesh);
  }

  return { group, playStrings, sympathetics };
}

export function buildSarangi(mats) {
  const root = new THREE.Group();
  root.name = 'sarangi';

  const body = createBody(mats);
  const neck = createNeck(mats);
  const box = createPegbox(mats);
  const head = createHead(mats);
  const tail = createTailpiece(mats);
  const side = createSidePegs(mats, neck);

  const strap = new THREE.Mesh(new THREE.BoxGeometry(0.168, 0.014, 0.0042), mats.leather);
  strap.position.set(0.0, 0.12, body.skinZ + 0.007);
  strap.castShadow = true;

  const elephant = createElephant(mats);
  elephant.position.set(0.0, 0.124, body.skinZ + 0.015);
  elephant.rotation.set(0, 0, 0);
  elephant.scale.setScalar(1.7);

  const strings = createStrings(mats, {
    skinZ: body.skinZ,
    sundari: neck.sundari,
    nutY: neck.nutY,
    largePegs: box.largePegs,
    smallPegs: box.smallPegs,
    tabijY: box.tabijY,
  });

  root.add(body.group, neck.group, box.group, head, tail, side.group, strap, elephant, strings.group);

  const hitRoot = new THREE.Group();
  strings.playStrings.forEach((s) => hitRoot.add(s.hit));
  root.add(hitRoot);

  root.userData = {
    playStrings: strings.playStrings,
    sympathetics: strings.sympathetics,
    totalHeight: TOTAL,
    bridgeY: 0.118,
  };

  root.position.y = 0.01;
  return root;
}
